import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, call } from "@/core/api/client";
import { ENDPOINTS } from "@/core/api/endpoints";
import { QUERY_KEYS } from "@/core/api/query-client";
import { addDays } from "@/core/format/format";

import { SUBSCRIPTION_LIMITS, type SubscriptionStatus } from "./subscription-display";

export const SUBSCRIPTION_PAGE_SIZE = 25;

export interface SubscriptionFilters {
  search?: string;
  status?: SubscriptionStatus;
  brandId?: string;
  userId?: string;
}

export function useSubscriptionList(filters: SubscriptionFilters) {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.subscriptions.list(filters),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      call(
        api.GET(ENDPOINTS.adminSubscriptions, {
          params: {
            query: {
              limit: SUBSCRIPTION_PAGE_SIZE,
              ...(pageParam ? { cursor: pageParam } : {}),
              ...(filters.search ? { search: filters.search } : {}),
              ...(filters.status ? { status: filters.status } : {}),
              ...(filters.brandId ? { brandId: filters.brandId } : {}),
              ...(filters.userId ? { userId: filters.userId } : {}),
            },
          },
        }),
      ),
    getNextPageParam: (page) => page.meta.nextCursor ?? undefined,
  });
}

/**
 * One plan with the meals of one window: `from` and the following days (the API allows at most 62 days per
 * call). The previous window stays on screen while the next one loads, so nothing jumps.
 */
export function useSubscription(subscriptionId: string, from?: string) {
  return useQuery({
    queryKey: QUERY_KEYS.subscriptions.detail(subscriptionId, from),
    queryFn: async () =>
      (
        await call(
          api.GET(ENDPOINTS.adminSubscription, {
            params: {
              path: { subscriptionId },
              query: from ? { from, to: addDays(from, SUBSCRIPTION_LIMITS.mealRangeDays - 1) } : {},
            },
          }),
        )
      ).data,
    placeholderData: (previous) => previous,
  });
}

function useSubscriptionChanged() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscriptions.all });
    void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users.all });
    void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.overview });
  };
}

export function useAddBonusMeals(subscriptionId: string) {
  const changed = useSubscriptionChanged();
  return useMutation({
    mutationFn: async (input: { count: number; slotId: string; reason: string }) =>
      (
        await call(
          api.POST(ENDPOINTS.adminSubscriptionBonusMeals, {
            params: { path: { subscriptionId } },
            body: input,
          }),
        )
      ).data,
    onSuccess: changed,
  });
}

export function useCancelSubscription(subscriptionId: string) {
  const changed = useSubscriptionChanged();
  return useMutation({
    mutationFn: async (reason: string) =>
      (
        await call(
          api.POST(ENDPOINTS.adminSubscriptionCancel, {
            params: { path: { subscriptionId } },
            body: { reason },
          }),
        )
      ).data,
    onSuccess: changed,
  });
}

/** Takes meals off the end of the plan (only ones that haven't closed). */
export function useRemoveMeals(subscriptionId: string) {
  const changed = useSubscriptionChanged();
  return useMutation({
    mutationFn: async (input: { count: number; reason: string }) =>
      (
        await call(
          api.POST(ENDPOINTS.adminSubscriptionRemoveMeals, {
            params: { path: { subscriptionId } },
            body: input,
          }),
        )
      ).data,
    onSuccess: changed,
  });
}

/** Makes the plan have exactly this many meals left (removes from the end, or adds free meals). */
export function useSetMealsLeft(subscriptionId: string) {
  const changed = useSubscriptionChanged();
  return useMutation({
    mutationFn: async (input: { mealsLeft: number; reason: string }) =>
      (
        await call(
          api.PUT(ENDPOINTS.adminSubscriptionMealsLeft, {
            params: { path: { subscriptionId } },
            body: input,
          }),
        )
      ).data,
    onSuccess: changed,
  });
}

/** Moves the customer to another plan of the same brand (meals left carry over; dish choices are cleared). */
export function useMovePlan(subscriptionId: string) {
  const changed = useSubscriptionChanged();
  return useMutation({
    mutationFn: async (input: { planId: string; reason: string }) =>
      (
        await call(
          api.POST(ENDPOINTS.adminSubscriptionMovePlan, {
            params: { path: { subscriptionId } },
            body: input,
          }),
        )
      ).data,
    onSuccess: changed,
  });
}
